/**
 * V108-6 setup screens — API client for enablers (setup steps), coverage, the no-source list,
 * consent started (625), declared production accounts (626) and discovery fact suggestions.
 * Every route is workspace-scoped (`/api/w/:slug/...`); the server takes the workspace from the
 * session (workspaceContext) and 404s on any miss.
 */
import type { EnablerProgress, NoSourceList } from '@aigovops/core';
import { req } from './api';

const ws = (slug: string) => `/api/w/${encodeURIComponent(slug)}`;

export type EnablerState = 'suggested' | 'in_progress' | 'done' | 'declined' | 'blocked' | 'snoozed';
export type EnablerActor = 'repo_agent' | 'admin_console' | 'collector';

export interface EnablerItem {
  id: string;
  code: string;
  subject_key: string;
  environment: string | null;
  state: EnablerState;
  reason: string | null;
  snoozed_until: string | null;
  verified_at: string | null;
  assignee_user_id: string | null;
  assigned_at: string | null;
  assigned_by: string | null;
  updated_at: string;
  rank: number | null;
  actor: EnablerActor;
  title: string;
  why: string;
  effort_min: number;
  privilege: { level: 'read_only' } | { level: 'beyond_read_only'; reason: string };
  instructions: { command: string | null; version_pinned: boolean; steps: readonly string[]; secret_names: readonly string[] };
  unlocks: {
    objectives: string[]; programs: Record<string, string[]>; inventory_item_ids: string[]; blueprint_slots: string[];
    /** V108-8-8: programs with open objectives where no test takes this step's evidence (absent from older Workers). */
    not_taken_by?: Array<{ program: string; reason: 'no_test_takes_this_evidence'; has_manual?: boolean }>;
  };
  objectives_unlocked: number;
  progress: EnablerProgress;
  /** V108-8-20: what the step is about, by name (a CI gate step: its system). Absent from older Workers. */
  subject_label?: string | null;
}

export interface EnablersResponse {
  enablers: EnablerItem[];
  cli_version_pinned: boolean;
  cli_version?: string | null;
  generated_at?: string;
  /** V108-8-21: when the stored view was computed (null until the first compute), and whether a newer one is on its way. */
  computed_at?: string | null;
  computing?: boolean;
}

export interface CoverageTrendPoint {
  snapshot_date: string;
  objectives_in_scope: number;
  connected: number;
  automatable_gaps: number;
  manual_only: number;
  coverage_pct: number | null;
}

export interface CoverageProgram {
  program: string;
  label: string;
  profile_id: string;
  objectives_in_scope: number;
  connected: number;
  automatable_gaps: number;
  manual_only: number;
  excluded: number;
  coverage_pct: number | null;
  next_enabler: { code: string; subject_key: string; title: string; objectives_unlocked: number } | null;
  preview_only?: number;
  partial?: number;
  trend?: CoverageTrendPoint[];
}

export interface CoverageResponse { programs: CoverageProgram[]; generated_at: string; computed_at?: string | null; computing?: boolean }

export type EnablerPatch =
  | { state: 'in_progress' | 'reopen' }
  | { state: 'declined'; reason: string }
  | { state: 'snoozed'; until: string; reason?: string | null }
  | { assignee_user_id: string | null };

export const listEnablers = (slug: string) => req<EnablersResponse>(`${ws(slug)}/enablers`);
export const getCoverage = (slug: string) => req<CoverageResponse>(`${ws(slug)}/coverage`);
export const getNoSource = (slug: string, program: string) =>
  req<{ no_source: NoSourceList }>(`${ws(slug)}/enablers/no-source?program=${encodeURIComponent(program)}`);
export const patchEnabler = (slug: string, id: string, body: EnablerPatch) =>
  req<{ enabler: Partial<EnablerItem> & { id: string } }>(`${ws(slug)}/enablers/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) });
export const setConsentStarted = (slug: string, connectorId: string, started: boolean) =>
  req<{ connector_id: string; consent_started_at: string | null }>(`${ws(slug)}/connectors/${encodeURIComponent(connectorId)}/consent`, { method: 'POST', body: JSON.stringify({ started }) });
export const setConnectorEnvironment = (slug: string, connectorId: string, environment: string) =>
  req<{ connector_id: string; environment: string }>(`${ws(slug)}/connectors/${encodeURIComponent(connectorId)}`, { method: 'PATCH', body: JSON.stringify({ environment }) });
/** V108-8-13: trust the workflow a verified-but-untrusted signed run came from (owner / admin; the server uses the observed values). */
export const trustObservedRun = (slug: string, observationId: string, confirmOwner?: string) =>
  req<{ identity: { identity_id: string; repository: string; workflow_path: string; ref: string } }>(`${ws(slug)}/settings/ci-identities/observations/${encodeURIComponent(observationId)}/trust`,
    { method: 'POST', body: JSON.stringify(confirmOwner ? { confirm_owner: confirmOwner } : {}) });
/** V108-8-13 review M-1: "Not ours" (owner / admin, audited): never suggest this observed workflow again. */
export const dismissObservedRun = (slug: string, observationId: string) =>
  req<{ dismissed: true }>(`${ws(slug)}/settings/ci-identities/observations/${encodeURIComponent(observationId)}/dismiss`, { method: 'POST', body: '{}' });
export const runConnectorCheck = (slug: string, connectorId: string) =>
  req<{ status?: string; workflow_id?: string }>(`${ws(slug)}/connectors/${encodeURIComponent(connectorId)}/run`, { method: 'POST', body: '{}' });

export interface CloudAccount { provider_key: 'aws'; account_id: string; label: string | null; state: 'production' | 'not_production' }
export const listCloudAccounts = (slug: string) => req<{ accounts: CloudAccount[] }>(`${ws(slug)}/cloud-accounts`);
export const declareAwsAccounts = (slug: string, accounts: Array<{ account_id: string; label?: string | null }>) =>
  req<{ accounts: CloudAccount[] }>(`${ws(slug)}/cloud-accounts/aws`, { method: 'PUT', body: JSON.stringify({ accounts }) });
export const setAwsAccountState = (slug: string, accountId: string, state: CloudAccount['state']) =>
  req<{ account: CloudAccount }>(`${ws(slug)}/cloud-accounts/aws/${encodeURIComponent(accountId)}`, { method: 'PATCH', body: JSON.stringify({ state }) });

/** "Send to <owner>" for a no-source objective: the requirement's owner hat (v9.8 assignments). */
export const assignRequirementOwner = (slug: string, requirementId: string, userId: string) =>
  req<{ assignment: { assignment_id: string } }>(`${ws(slug)}/assignments`, {
    method: 'POST', body: JSON.stringify({ subject_kind: 'requirement', subject_id: requirementId, responsibility: 'owner', user_id: userId }),
  });

export interface FactSuggestion {
  suggestion_id: string;
  system_id: string;
  fact_key: 'internet_reachable' | 'providers' | 'environments';
  value_json: string;
  detected_by: string;
  status: string;
}
export const listFactSuggestions = (slug: string, systemId?: string) =>
  req<{ suggestions: FactSuggestion[]; can_confirm: boolean }>(`${ws(slug)}/fact-suggestions${systemId ? `?system_id=${encodeURIComponent(systemId)}` : ''}`);

/** The caller's own hats (v9.8 assignments): a program lead may change setup steps like an owner / admin. */
export const getMyHats = (slug: string) => req<{ responsibilities: string[] }>(`${ws(slug)}/assignments/me`);
